import { ApolloClient } from "@apollo/client";

/**
 * Service for exporting large datasets by fetching data in batches.
 */
export class ExportService<T> {
  private client: ApolloClient<unknown>;
  private options: ExportServiceOptions<T> &
    Required<Pick<ExportServiceOptions<T>, "batchSize" | "warningThreshold" | "maxRowsLimit">>;
  private abortController: AbortController | null = null;

  constructor(client: ApolloClient<unknown>, options: ExportServiceOptions<T>) {
    this.client = client;
    this.options = {
      batchSize: 100,
      warningThreshold: 5000,
      maxRowsLimit: 10000,
      ...options,
    };
  }

  /**
   * Determines if the export size requires a warning or exceeds the maximum limit.
   */
  public async checkExportSize(): Promise<{ shouldWarn: boolean; exceedsLimit: boolean; total: number }> {
    const total = await this.options.getTotalCount();

    return {
      shouldWarn: total >= this.options.warningThreshold,
      exceedsLimit: total > this.options.maxRowsLimit,
      total,
    };
  }

  /**
   * Cancels the ongoing export operation.
   */
  public cancelExport(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  /**
   * Exports all data matching the current filter criteria.
   * Fetches data in batches and returns a CSV string.
   */
  public async exportData(): Promise<string> {
    const { total, exceedsLimit } = await this.checkExportSize();

    if (exceedsLimit) {
      throw new Error(`Export exceeds maximum limit of ${this.options.maxRowsLimit} rows.`);
    }

    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    let allData: T[] = [];
    const batchSize = this.options.batchSize || 100;

    try {
      for (let offset = 0; offset < total; offset += batchSize) {
        if (signal.aborted) {
          throw new Error("Export was cancelled");
        }

        const batch = await this.options.fetchDataBatch(offset, batchSize);
        allData = [...allData, ...batch];

        if (this.options.onProgress) {
          this.options.onProgress(allData.length, total);
        }
      }

      const csvData = this.options.transformDataToCSV
        ? this.options.transformDataToCSV(allData)
        : this.defaultTransformToCSV(allData);

      return csvData;
    } catch (error) {
      if (signal.aborted) {
        throw new Error("Export was cancelled");
      }
      throw error;
    } finally {
      this.abortController = null;
    }
  }

  /**
   * Default method to transform data to CSV format if not provided by the user.
   */
  private defaultTransformToCSV(data: T[]): string {
    if (!data || data.length === 0) {
      return "";
    }

    // Get headers from the first object
    const headers = Object.keys(data[0] as Record<string, unknown>);

    // Create CSV rows
    const rows = data.map((item) => {
      const record = item as Record<string, unknown>;
      return headers
        .map((header) => {
          const value = record[header];
          // Handle different types and escape quotes in strings
          if (value === null || value === undefined) {
            return "";
          } else if (typeof value === "string") {
            return `"${value.replace(/"/g, '""')}"`;
          } else {
            return String(value);
          }
        })
        .join(",");
    });

    // Combine headers and rows
    return [headers.join(","), ...rows].join("\n");
  }

  /**
   * Initiates a download of the exported data.
   */
  public static downloadCSV(csvData: string, fileName: string): void {
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `${fileName}.csv`);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL object
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 100);
  }
}

export interface ExportServiceOptions<T> {
  /**
   * Function to fetch the total count
   */
  getTotalCount: () => Promise<number>;

  /**
   * Function to fetch data batches
   */
  fetchDataBatch: (offset: number, limit: number) => Promise<T[]>;

  /**
   * Function to transform data to CSV
   */
  transformDataToCSV?: (data: T[]) => string;

  /**
   * Batch size for fetching data
   * @default 100
   */
  batchSize?: number;

  /**
   * Warning threshold
   * @default 5000
   */
  warningThreshold?: number;

  /**
   * Maximum rows limit
   * @default 10000
   */
  maxRowsLimit?: number;

  /**
   * Function to update progress
   */
  onProgress?: (current: number, total: number) => void;

  /**
   * File name for the export
   */
  fileName?: string;
}
